export class AuthScreen {
  constructor(ctx) {
    this.ctx = ctx;
    this.mode = 'login';
    this.root = null;
    this.form = null;
    this.fieldsRoot = null;
    this.socialRoot = null;
    this.socialActions = null;
    this.fields = new Map();
    this.providerButtons = [];
    this._loading = false;
    this._oauthLoading = false;
    this._open = false;
    this._providersLoadId = 0;
    this._oauthWindow = null;
    this._oauthMessageHandler = null;
    this._oauthClosePoll = null;
    this._build();
  }

  async init() {
    this._onKeyDown = (event) => {
      if (!this._open || event.code !== 'Escape') return;
      event.preventDefault();
      this.close();
    };
    window.addEventListener('keydown', this._onKeyDown);
  }

  open(mode = 'login') {
    this.mode = mode === 'register' ? 'register' : 'login';
    this._open = true;
    this._renderMode();
    this._hideSocial();
    this.root.hidden = false;
    this.root.classList.add('open');
    this.fields.get('email')?.input?.focus?.();
    void this._loadProviders();
  }

  close() {
    this._open = false;
    this._providersLoadId++;
    this._stopOAuth(true);
    this._setOAuthLoading(false);
    this.root?.classList?.remove?.('open');
    if (this.root) this.root.hidden = true;
    this._clearErrors();
  }

  dispose() {
    this._open = false;
    this._providersLoadId++;
    this._stopOAuth(true);
    if (this._onKeyDown) window.removeEventListener('keydown', this._onKeyDown);
    this.root?.remove?.();
    this.fields.clear();
    this.providerButtons.length = 0;
    this.root = null;
    this.socialRoot = null;
    this.socialActions = null;
  }

  _build() {
    const root = document.createElement('div');
    root.className = 'net-screen';
    root.hidden = true;
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-labelledby', 'auth-title');

    const panel = document.createElement('div');
    panel.className = 'net-panel';
    root.appendChild(panel);

    const eyebrow = document.createElement('div');
    eyebrow.className = 'net-sub';
    eyebrow.textContent = 'CONTA DE OPERADOR';
    panel.appendChild(eyebrow);

    this.title = document.createElement('h2');
    this.title.id = 'auth-title';
    this.title.className = 'net-title';
    panel.appendChild(this.title);

    this.description = document.createElement('div');
    this.description.className = 'net-sub';
    panel.appendChild(this.description);

    this.socialRoot = document.createElement('div');
    this.socialRoot.className = 'net-form';
    this.socialRoot.hidden = true;
    panel.appendChild(this.socialRoot);

    this.socialActions = document.createElement('div');
    this.socialActions.className = 'net-actions';
    this.socialRoot.appendChild(this.socialActions);

    const separator = document.createElement('div');
    separator.className = 'net-sub';
    separator.textContent = 'OU';
    this.socialRoot.appendChild(separator);

    const form = document.createElement('form');
    form.className = 'net-form';
    form.noValidate = true;
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void this._submit();
    });
    panel.appendChild(form);
    this.form = form;

    this.fieldsRoot = document.createElement('div');
    form.appendChild(this.fieldsRoot);

    this.generalError = document.createElement('div');
    this.generalError.className = 'net-error';
    this.generalError.setAttribute('role', 'alert');
    this.generalError.hidden = true;
    form.appendChild(this.generalError);

    const actions = document.createElement('div');
    actions.className = 'net-actions';
    form.appendChild(actions);

    this.submitButton = document.createElement('button');
    this.submitButton.type = 'submit';
    this.submitButton.className = 'btn primary btn-primary';
    actions.appendChild(this.submitButton);

    this.switchButton = document.createElement('button');
    this.switchButton.type = 'button';
    this.switchButton.className = 'net-link';
    this.switchButton.addEventListener('click', () => {
      if (this._loading || this._oauthLoading) return;
      this.mode = this.mode === 'login' ? 'register' : 'login';
      this._renderMode();
    });
    actions.appendChild(this.switchButton);

    this.continueButton = document.createElement('button');
    this.continueButton.type = 'button';
    this.continueButton.className = 'net-link';
    this.continueButton.textContent = 'CONTINUAR SEM CONTA';
    this.continueButton.addEventListener('click', () => this.close());
    actions.appendChild(this.continueButton);

    (document.getElementById('ui-root') || document.body).appendChild(root);
    this.root = root;
    this._renderMode();
  }

  async _loadProviders() {
    const api = this.ctx?.session?.api || this.ctx?.api;
    const loadId = ++this._providersLoadId;
    let providers = [];

    if (typeof api?.providers === 'function') {
      try {
        providers = await api.providers();
      } catch (err) {
        console.warn('[AuthScreen] nao foi possivel carregar provedores de login:', err);
      }
    }

    if (!this._open || loadId !== this._providersLoadId) return;
    const available = Array.isArray(providers)
      ? providers.filter((provider) => provider?.id && provider?.name)
      : [];
    if (available.length === 0) return;

    this.providerButtons.length = 0;
    this.socialActions.replaceChildren();
    for (const provider of available) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'btn primary btn-primary';
      button.textContent = `CONTINUAR COM ${provider.name}`;
      button.addEventListener('click', () => void this._startOAuth(provider.id));
      this.socialActions.appendChild(button);
      this.providerButtons.push(button);
    }
    this.socialRoot.hidden = false;
    this._syncBusyControls();
  }

  _hideSocial() {
    this.providerButtons.length = 0;
    this.socialActions?.replaceChildren?.();
    if (this.socialRoot) this.socialRoot.hidden = true;
  }

  async _startOAuth(providerId) {
    if (this._loading || this._oauthLoading) return;
    this._clearErrors();

    const api = this.ctx?.session?.api || this.ctx?.api;
    if (typeof api?.oauthUrl !== 'function' || typeof this.ctx?.session?.set !== 'function') {
      this._setGeneralError('O SERVI\u00c7O DE CONTA N\u00c3O EST\u00c1 DISPON\u00cdVEL.');
      return;
    }

    this._setOAuthLoading(true);
    const width = 600;
    const height = 700;
    const screenLeft = Number.isFinite(window.screenLeft) ? window.screenLeft : (window.screenX || 0);
    const screenTop = Number.isFinite(window.screenTop) ? window.screenTop : (window.screenY || 0);
    const outerWidth = window.outerWidth || document.documentElement.clientWidth;
    const outerHeight = window.outerHeight || document.documentElement.clientHeight;
    const left = Math.round(screenLeft + (outerWidth - width) / 2);
    const top = Math.round(screenTop + (outerHeight - height) / 2);
    const features = `popup=yes,width=${width},height=${height},left=${left},top=${top}`;

    this._oauthMessageHandler = (event) => {
      if (event.origin !== location.origin) return;
      if (event.source !== this._oauthWindow) return;
      const data = event.data;
      if (!data || data.type !== 'oauth') return;

      this._stopOAuth(false);
      this._setOAuthLoading(false);
      if (data.user && data.token) {
        this.ctx.session.set(data.user, data.token);
        this.form?.reset?.();
        this.close();
        return;
      }

      const message = typeof data.message === 'string' && data.message.trim()
        ? data.message
        : 'N\u00c3O FOI POSS\u00cdVEL ENTRAR COM ESTE PROVEDOR. TENTE NOVAMENTE.';
      this._setGeneralError(message);
    };
    window.addEventListener('message', this._oauthMessageHandler);

    try {
      this._oauthWindow = window.open(api.oauthUrl(providerId), `oauth-${providerId}`, features);
    } catch (err) {
      console.warn('[AuthScreen] nao foi possivel abrir a janela de login:', err);
      this._stopOAuth(false);
      this._setOAuthLoading(false);
      this._setGeneralError('N\u00c3O FOI POSS\u00cdVEL ABRIR A JANELA DE LOGIN. TENTE NOVAMENTE.');
      return;
    }

    if (!this._oauthWindow) {
      this._stopOAuth(false);
      this._setOAuthLoading(false);
      this._setGeneralError('PERMITA JANELAS POP-UP PARA ENTRAR COM ESTE PROVEDOR.');
      return;
    }

    this._oauthClosePoll = window.setInterval(() => {
      if (!this._oauthWindow?.closed) return;
      this._stopOAuth(false);
      this._setOAuthLoading(false);
    }, 250);
    this._oauthWindow.focus?.();
  }

  _stopOAuth(closeWindow) {
    if (this._oauthClosePoll !== null) {
      window.clearInterval(this._oauthClosePoll);
      this._oauthClosePoll = null;
    }
    if (this._oauthMessageHandler) {
      window.removeEventListener('message', this._oauthMessageHandler);
      this._oauthMessageHandler = null;
    }
    const popup = this._oauthWindow;
    this._oauthWindow = null;
    if (closeWindow && popup && !popup.closed) popup.close();
  }

  _setOAuthLoading(loading) {
    this._oauthLoading = loading;
    this._syncBusyControls();
  }

  _syncBusyControls() {
    const busy = this._loading || this._oauthLoading;
    if (this.submitButton) this.submitButton.disabled = busy;
    for (const button of this.providerButtons) button.disabled = busy;
  }

  _renderMode() {
    const register = this.mode === 'register';
    this.title.textContent = register ? 'CRIAR CONTA' : 'ENTRAR';
    this.description.textContent = register
      ? 'IDENTIFIQUE-SE PARA ENTRAR NO COMBATE MULTIJOGADOR.'
      : 'USE SUA CONTA PARA ACESSAR O MULTIJOGADOR.';
    this.submitButton.textContent = register ? 'CRIAR CONTA' : 'ENTRAR';
    this.switchButton.textContent = register ? 'J\u00c1 TENHO CONTA' : 'CRIAR CONTA';

    this.fields.clear();
    this.fieldsRoot.replaceChildren();
    this.fieldsRoot.appendChild(this._field('email', 'E-MAIL', 'email', 'email'));

    if (register) {
      this.fieldsRoot.appendChild(this._field('name', 'NOME', 'text', 'name'));
      this.fieldsRoot.appendChild(this._field('nickname', 'APELIDO', 'text', 'nickname'));
      this.fieldsRoot.appendChild(this._field('password', 'SENHA', 'password', 'new-password'));
      this.fieldsRoot.appendChild(this._field('confirmPassword', 'CONFIRMAR SENHA', 'password', 'new-password'));
    } else {
      this.fieldsRoot.appendChild(this._field('password', 'SENHA', 'password', 'current-password'));
    }

    this._clearErrors();
    if (this._open) this.fields.get('email')?.input?.focus?.();
  }

  _field(name, labelText, type, autocomplete) {
    const row = document.createElement('div');
    row.className = 'net-field';

    const id = `auth-${name}`;
    const label = document.createElement('label');
    label.className = 'net-label';
    label.htmlFor = id;
    label.textContent = labelText;
    row.appendChild(label);

    const input = document.createElement('input');
    input.id = id;
    input.name = name;
    input.type = type;
    input.autocomplete = autocomplete;
    input.className = 'net-input';
    input.setAttribute('aria-describedby', `${id}-error`);
    row.appendChild(input);

    const error = document.createElement('div');
    error.id = `${id}-error`;
    error.className = 'net-error';
    error.hidden = true;
    row.appendChild(error);

    this.fields.set(name, { input, error });
    return row;
  }

  _values() {
    return {
      email: this.fields.get('email')?.input?.value?.trim?.() || '',
      name: this.fields.get('name')?.input?.value?.trim?.() || '',
      nickname: this.fields.get('nickname')?.input?.value?.trim?.() || '',
      password: this.fields.get('password')?.input?.value || '',
      confirmPassword: this.fields.get('confirmPassword')?.input?.value || ''
    };
  }

  _validate(values) {
    const errors = {};
    const emailPattern = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;

    if (!emailPattern.test(values.email)) errors.email = 'INFORME UM E-MAIL V\u00c1LIDO.';
    if (this.mode === 'register') {
      if (values.name.length < 2 || values.name.length > 60) {
        errors.name = 'O NOME DEVE TER DE 2 A 60 CARACTERES.';
      }
      if (!/^[A-Za-z0-9_.-]{3,20}$/.test(values.nickname)) {
        errors.nickname = 'USE DE 3 A 20 LETRAS, N\u00daMEROS, PONTO, H\u00cdFEN OU UNDERLINE.';
      }
    }
    if (values.password.length < 8) errors.password = 'A SENHA DEVE TER NO M\u00cdNIMO 8 CARACTERES.';
    if (this.mode === 'register' && values.confirmPassword !== values.password) {
      errors.confirmPassword = 'AS SENHAS N\u00c3O CONFEREM.';
    }

    return errors;
  }

  async _submit() {
    if (this._loading || this._oauthLoading) return;

    this._clearErrors();
    const values = this._values();
    const errors = this._validate(values);
    const firstError = Object.keys(errors)[0];
    if (firstError) {
      for (const [field, message] of Object.entries(errors)) this._setFieldError(field, message);
      this.fields.get(firstError)?.input?.focus?.();
      return;
    }

    const api = this.ctx?.session?.api || this.ctx?.api;
    if (!api) {
      this._setGeneralError('O SERVI\u00c7O DE CONTA N\u00c3O EST\u00c1 DISPON\u00cdVEL.');
      return;
    }

    this._setLoading(true);
    try {
      const result = this.mode === 'register'
        ? await api.register?.({
            email: values.email,
            name: values.name,
            nickname: values.nickname,
            password: values.password
          })
        : await api.login?.({ email: values.email, password: values.password });

      if (!result?.user || !result?.token || !this.ctx?.session?.set) {
        throw new Error('Resposta de autenticacao incompleta.');
      }

      this.ctx.session.set(result.user, result.token);
      this.form?.reset?.();
      this.close();
    } catch (err) {
      const field = err?.field || (err?.code === 'email_taken' ? 'email' : null) ||
        (err?.code === 'nickname_taken' ? 'nickname' : null);
      if (field && this.fields.has(field)) {
        this._setFieldError(field, err?.message || 'VERIFIQUE ESTE CAMPO.');
        this.fields.get(field)?.input?.focus?.();
      } else if (err?.code === 'bad_credentials') {
        this._setFieldError('password', err?.message || 'E-MAIL OU SENHA INCORRETOS.');
      } else {
        this._setGeneralError(err?.message || 'N\u00c3O FOI POSS\u00cdVEL CONCLUIR. TENTE NOVAMENTE.');
      }
    } finally {
      this._setLoading(false);
    }
  }

  _setLoading(loading) {
    this._loading = loading;
    this._syncBusyControls();
    this.submitButton.textContent = loading
      ? 'CARREGANDO...'
      : (this.mode === 'register' ? 'CRIAR CONTA' : 'ENTRAR');
  }

  _clearErrors() {
    for (const { input, error } of this.fields.values()) {
      input.removeAttribute('aria-invalid');
      error.textContent = '';
      error.hidden = true;
    }
    if (this.generalError) {
      this.generalError.textContent = '';
      this.generalError.hidden = true;
    }
  }

  _setFieldError(field, message) {
    const target = this.fields.get(field);
    if (!target) return;
    target.input.setAttribute('aria-invalid', 'true');
    target.error.textContent = message;
    target.error.hidden = false;
  }

  _setGeneralError(message) {
    this.generalError.textContent = message;
    this.generalError.hidden = false;
  }
}
